import React, { useEffect, useState, useRef, useMemo } from "react";
import {
  Box,
  IconButton,
  Tooltip,
  Modal,
  Typography,
  Chip,
  Skeleton,
  alpha,
  useTheme,
} from "@mui/material";
import {
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon,
  // Refresh as RefreshIcon,
  Fullscreen as FullscreenIcon,
  Close as CloseIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Videocam as VideocamIcon,
} from "@mui/icons-material";
import GifPlayer from "@deepit/react-gif-player";
import { backend_url } from "../App";

export default function CameraFeed(props) {
  const theme = useTheme();
  const [picture, setPicture] = useState({ pic: "", rnd: 0 });
  const cameraId = props.cameraId;
  // const stream = props.liveStream;
  const [imageUrls, setImageUrls] = useState(props.imageUrls || []);
  const [loading, setLoading] = useState(true);
  // const iframeRef = useRef(null);
  const [isPaused, setPaused] = useState(false);
  const [currentFrame, setCurrentFrame] = useState(0);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [timerInterval] = useState(1000);
  const [timerId, setTimerId] = useState(null);
  const [isOverflow, setIsOverflow] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const scrollContainerRef = useRef(null);

  const config = useMemo(
    () => ({
      headers: {
        Authorization: `Token ${localStorage.getItem("token")}`,
      },
    }),
    [],
  );

  // const refreshImage = () => {
  //   if (iframeRef.current) {
  //     iframeRef.current.src = props.liveStream;
  //   }
  // };

  const checkOverflow = () => {
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      setIsOverflow(scrollContainer.scrollWidth > scrollContainer.clientWidth);
    }
  };

  const pauseGif = () => {
    setPaused(true);
    if (timerId) {
      clearInterval(timerId);
      setTimerId(null);
    }
  };

  const playGif = () => {
    setPaused(false);
    const newTimerId = setInterval(() => {
      setCurrentFrame((prevFrame) => prevFrame + 1);
    }, timerInterval);
    setTimerId(newTimerId);
  };

  const togglePause = () => {
    if (isPaused) {
      playGif();
    } else {
      pauseGif();
    }
  };

  useEffect(() => {
    setImageUrls(props.imageUrls || []);
    if (!isPaused && props.imageUrls.length > 0) {
      const newTimerId = setInterval(() => {
        setCurrentFrame((prevFrame) => prevFrame + 1);
      }, timerInterval);
      setTimerId(newTimerId);
    } else if (timerId) {
      clearInterval(timerId);
      setTimerId(null);
    }
    return () => {
      if (timerId) {
        clearInterval(timerId);
      }
    };
  }, [timerInterval, isPaused, props.imageUrls, timerId]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      if (
        !isPaused &&
        Array.isArray(props.imageUrls) &&
        props.imageUrls.length > 0
      ) {
        setCurrentImageIndex(
          (prevIndex) => (prevIndex + 1) % props.imageUrls.length,
        );
      }
    }, timerInterval);
    return () => {
      clearInterval(intervalId);
    };
  }, [timerInterval, props.imageUrls, isPaused]);

  useEffect(() => {
    checkOverflow();
  }, [props.imageUrls]);

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollLeft -= 100;
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollLeft += 100;
    }
  };

  // Fetch camera data on mount
  useEffect(() => {
    const fetchCameraData = async () => {
      const randNum = Math.random() * 10000000;
      let fetchUrl = "";

      if (cameraId.link !== "") {
        let link = cameraId.link;
        if (link.includes("http://127.0.0.1:8000")) {
          link = link.replace(
            "http://127.0.0.1:8000",
            "https://api.forestwatch.org.pk",
          );
        }
        setPicture({ pic: link, rnd: randNum });
        setLoading(false);
        return;
      } else if (cameraId.cam === "Event") {
        setPicture({ pic: cameraId.link, rnd: randNum });
        setLoading(false);
        return;
      } else {
        fetchUrl = `${backend_url}/core/api/camera/${cameraId.id}`;
      }

      try {
        const response = await fetch(fetchUrl, config);
        if (!response.ok) {
          throw new Error(`HTTP error: ${response.status}`);
        }
        const actualData = await response.json();
        let imageLink = actualData.live_image;
        if (imageLink === null || imageLink === "") {
          setPicture({ pic: `/video.png?ver=${randNum}`, rnd: randNum });
        } else {
          if (imageLink.includes("http://127.0.0.1:8000")) {
            imageLink = imageLink.replace(
              "http://127.0.0.1:8000",
              "https://api.forestwatch.org.pk",
            );
          }
          setPicture({ pic: `${imageLink}?ver=${randNum}`, rnd: randNum });
        }
      } catch (err) {
        console.log(err);
        setPicture({ pic: "", rnd: Math.random() * 10000000 });
      } finally {
        setLoading(false);
      }
    };

    fetchCameraData();
  }, [cameraId, config]);

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "90%",
    maxWidth: 900,
    bgcolor: "background.paper",
    borderRadius: 4,
    boxShadow: 24,
    p: 3,
    outline: "none",
  };

  // Always show live stream if available, otherwise show event GIF
  // const isLiveStreamAvailable = stream && stream !== "";

  return (
    <Box sx={{ height: "100%", position: "relative" }}>
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 400,
          }}
        >
          <Skeleton
            variant="rectangular"
            width="100%"
            height={400}
            sx={{ borderRadius: 3 }}
          />
        </Box>
      ) : (
        <Box>
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: 2,
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {/* {isLiveStreamAvailable ? (
                <Chip
                  icon={<VideocamIcon sx={{ fontSize: 16 }} />}
                  label="Live Stream"
                  size="small"
                  color="error"
                  sx={{ fontWeight: 600 }}
                />
              ) : ( */}
              <Chip
                icon={<VideocamIcon sx={{ fontSize: 16 }} />}
                label={cameraId.cam || "Event"}
                size="small"
                color="primary"
                sx={{ fontWeight: 600 }}
              />
              {/* )} */}
            </Box>
          </Box>

          {/* Main Content */}
          <Box sx={{ p: 2 }}>
            {/* {isLiveStreamAvailable ? (
              // Live Stream View
              <Box sx={{ position: "relative" }}>
                <iframe
                  ref={iframeRef}
                  src={stream}
                  title="Live Stream"
                  style={{
                    width: "100%",
                    height: 400,
                    borderRadius: 16,
                    border: "none",
                    backgroundColor: "#000",
                  }}
                  frameBorder="0"
                  allow="autoplay; fullscreen"
                />
                <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                  <Tooltip title="Refresh">
                    <IconButton
                      onClick={refreshImage}
                      size="small"
                      sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1) }}
                    >
                      <RefreshIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            ) : ( */}
            {/* // Event GIF View */}
            <Box>
              <Box sx={{ position: "relative" }}>
                <GifPlayer
                  gif={
                    props.imageUrls.length > 0
                      ? props.imageUrls[currentImageIndex]
                      : picture.pic
                  }
                  paused={isPaused}
                  onTogglePlay={togglePause}
                  autoPlay
                  frame={!isNaN(currentFrame) ? currentFrame : 0}
                  style={{
                    width: "100%",
                    height: 400,
                    borderRadius: 16,
                    objectFit: "contain",
                    backgroundColor: "#000",
                  }}
                  onClick={() => togglePause()}
                />
                <Tooltip title="Fullscreen">
                  <IconButton
                    onClick={() => setIsModalOpen(true)}
                    size="small"
                    sx={{
                      position: "absolute",
                      bottom: 16,
                      right: 16,
                      bgcolor: alpha(theme.palette.common.black, 0.6),
                      "&:hover": {
                        bgcolor: alpha(theme.palette.common.black, 0.8),
                      },
                    }}
                  >
                    <FullscreenIcon sx={{ color: "white", fontSize: 20 }} />
                  </IconButton>
                </Tooltip>
                {!isPaused && props.imageUrls.length > 0 && (
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: 16,
                      left: 16,
                      bgcolor: alpha(theme.palette.common.black, 0.6),
                      borderRadius: 2,
                      px: 1,
                      py: 0.5,
                    }}
                  >
                    <Typography variant="caption" sx={{ color: "white" }}>
                      Playing
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Thumbnail Strip */}
              {props.imageUrls.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {isOverflow && (
                      <IconButton size="small" onClick={scrollLeft}>
                        <ChevronLeftIcon />
                      </IconButton>
                    )}
                    <Box
                      ref={scrollContainerRef}
                      sx={{
                        display: "flex",
                        overflowX: "auto",
                        gap: 1,
                        scrollBehavior: "smooth",
                        "&::-webkit-scrollbar": {
                          height: 6,
                        },
                        "&::-webkit-scrollbar-track": {
                          bgcolor: alpha(theme.palette.grey[500], 0.2),
                          borderRadius: 3,
                        },
                        "&::-webkit-scrollbar-thumb": {
                          bgcolor: alpha(theme.palette.primary.main, 0.5),
                          borderRadius: 3,
                        },
                        flex: 1,
                      }}
                    >
                      {props.imageUrls.map((imageUrl, index) => (
                        <Box
                          key={index}
                          onClick={() => setCurrentImageIndex(index)}
                          sx={{
                            flexShrink: 0,
                            cursor: "pointer",
                            borderRadius: 2,
                            overflow: "hidden",
                            border:
                              index === currentImageIndex
                                ? `2px solid ${theme.palette.primary.main}`
                                : `2px solid transparent`,
                            transition: "all 0.2s ease",
                            "&:hover": {
                              opacity: 0.8,
                            },
                          }}
                        >
                          <img
                            src={imageUrl}
                            alt={`Frame ${index + 1}`}
                            style={{
                              width: 80,
                              height: 60,
                              objectFit: "cover",
                            }}
                          />
                        </Box>
                      ))}
                    </Box>
                    {isOverflow && (
                      <IconButton size="small" onClick={scrollRight}>
                        <ChevronRightIcon />
                      </IconButton>
                    )}
                  </Box>
                </Box>
              )}
            </Box>
            {/* )} */}
          </Box>
        </Box>
      )}

      {/* Fullscreen Modal */}
      <Modal open={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <Box sx={modalStyle}>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
            <IconButton onClick={() => setIsModalOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={{ position: "relative" }}>
            <GifPlayer
              gif={
                props.imageUrls.length > 0
                  ? props.imageUrls[currentImageIndex]
                  : picture.pic
              }
              paused={isPaused}
              onTogglePlay={togglePause}
              autoPlay
              frame={!isNaN(currentFrame) ? currentFrame : 0}
              style={{
                width: "100%",
                maxHeight: "70vh",
                borderRadius: 16,
                objectFit: "contain",
                backgroundColor: "#000",
              }}
              onClick={() => togglePause()}
            />
          </Box>
          <Box sx={{ mt: 2 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Tooltip title="Previous">
                <IconButton
                  onClick={() =>
                    setCurrentImageIndex(
                      (prev) =>
                        (prev - 1 + props.imageUrls.length) %
                        props.imageUrls.length,
                    )
                  }
                  disabled={props.imageUrls.length === 0}
                >
                  <ChevronLeftIcon />
                </IconButton>
              </Tooltip>
              <Typography variant="caption" color="text.secondary">
                {currentImageIndex + 1} / {props.imageUrls.length}
              </Typography>
              <Tooltip title="Next">
                <IconButton
                  onClick={() =>
                    setCurrentImageIndex(
                      (prev) => (prev + 1) % props.imageUrls.length,
                    )
                  }
                  disabled={props.imageUrls.length === 0}
                >
                  <ChevronRightIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title={isPaused ? "Play" : "Pause"}>
                <IconButton onClick={togglePause}>
                  {isPaused ? <PlayArrowIcon /> : <PauseIcon />}
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
}
